import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Promo.module.css';
import { 
    CreatePromoInput,
    PromoType,
    promoService
} from '../../services/promo.service';
import { 
    roleBasedUsersService,
    User as RoleBasedUser
} from '../../services/roleBasedUsers.service';
import { authService } from '../../services/auth.service';

interface PromoFormData {
    name: string;
    amount: string;
    type: PromoType;
    expiresAt: string;
    shopIds: number[];
    minDeposit: string;
}

const initialFormState: PromoFormData = {
    name: '',
    amount: '',
    type: PromoType.REGISTRATION,
    expiresAt: '',
    shopIds: [],
    minDeposit: '',
};

const PromoCreate = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<PromoFormData>(initialFormState);
    const [shops, setShops] = useState<RoleBasedUser[]>([]);
    const [filteredShops, setFilteredShops] = useState<RoleBasedUser[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [hasPermission, setHasPermission] = useState(false);
    const [isShopUser, setIsShopUser] = useState(false);
    const [loadingShops, setLoadingShops] = useState(false);
    const [hasMoreShops, setHasMoreShops] = useState(true);
    const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
    const shopSelectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const checkPermission = authService.hasPermission('bonus.create');
        setHasPermission(checkPermission);

        if (!checkPermission) {
            return;
        }

        const currentUser = authService.getStoredUser();
        const isShop = currentUser?.role?.name === 'SHOP';
        setIsShopUser(isShop);

        if (isShop && currentUser?.id) {
            setFormData(prev => ({ ...prev, shopIds: [currentUser.id] }));
        } else {
            loadShops(true);
        }
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleShopToggle = (shopId: number) => {
        setFormData((prev) => ({
            ...prev,
            shopIds: prev.shopIds.includes(shopId)
                ? prev.shopIds.filter(id => id !== shopId)
                : [...prev.shopIds, shopId],
        }));
    };

    const loadShops = async (initial = false) => {
        if (loadingShops || (!initial && !hasMoreShops)) return;

        setLoadingShops(true);
        try {
            const response = await roleBasedUsersService.getUsersByRole(
                'SHOP',
                50,
                initial ? undefined : endCursor
            );

            if (initial) {
                setShops(response.users);
                setFilteredShops(response.users);
            } else {
                setShops(prev => [...prev, ...response.users]);
                setFilteredShops(prev => [...prev, ...response.users]);
            }

            setEndCursor(response.pageInfo.endCursor);
            setHasMoreShops(response.pageInfo.hasNextPage);
        } catch (error) {
            console.error('Error loading shops:', error);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (query.trim() === '') {
            setFilteredShops(shops);
        } else {
            const filtered = shops.filter(shop => {
                const searchLower = query.toLowerCase();
                return (
                    shop.username?.toLowerCase().includes(searchLower) ||
                    shop.name?.toLowerCase().includes(searchLower) ||
                    shop.id.toString().includes(query)
                );
            });
            setFilteredShops(filtered);
        }
    };

    const handleSelectAll = () => {
        const visibleShopIds = filteredShops.map(s => s.id);
        const allSelected = visibleShopIds.every(id => formData.shopIds.includes(id));

        if (allSelected) {
            setFormData(prev => ({
                ...prev,
                shopIds: prev.shopIds.filter(id => !visibleShopIds.includes(id))
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                shopIds: Array.from(new Set([...prev.shopIds, ...visibleShopIds]))
            }));
        }
    };

    const handleShopScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const element = e.currentTarget;
        const scrolledToBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

        if (scrolledToBottom && hasMoreShops && !loadingShops && searchQuery === '') {
            loadShops(false);
        }
    };

    const handleCancel = () => {
        navigate('/promo');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!isShopUser && formData.shopIds.length === 0) {
            setError('Please select at least one shop');
            return;
        }

        if (isShopUser && formData.shopIds.length === 0) {
            setError('Unable to determine shop ID');
            return;
        }

        if (!formData.expiresAt) {
            setError('Please select an expiration date');
            return;
        }

        const amount = parseFloat(formData.amount);
        if (isNaN(amount) || amount <= 0) {
            setError('Please enter a valid amount');
            return;
        }

        let minDeposit: number | undefined;
        if (formData.type === PromoType.DEPOSIT) {
            minDeposit = parseFloat(formData.minDeposit);
            if (isNaN(minDeposit) || minDeposit <= 0) {
                setError('Please enter a valid minimum deposit amount');
                return;
            }
        }

        setSubmitting(true);

        try {
            const input: CreatePromoInput = {
                shopIds: formData.shopIds,
                name: formData.name,
                expiresAt: new Date(formData.expiresAt).toISOString(),
                amount: amount,
                type: formData.type,
                minDeposit: minDeposit,
            };

            const createdPromoCodes = await promoService.createPromo(input);
            setSuccess(`Successfully created ${createdPromoCodes.length} promo code(s)!`);
            setTimeout(() => {
                navigate('/promo');
            }, 1500);
        } catch (err: any) {
            const errorMessage = err?.graphQLErrors?.[0]?.message || err?.message || 'Failed to create promo codes';
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    if (!hasPermission) {
        return (
            <main className={styles.main}>
                <div className={styles.noPermission}>
                    <h2>Access Denied</h2>
                    <p>You don't have permission to create promo codes.</p>
                    <p>Required permission: <strong>bonus.create</strong></p>
                </div>
            </main>
        );
    }

    return (
        <main className={styles.main}>
            <div className={styles.headerRow}>
                <h1 className={styles.title}>Create Promo Code</h1>
            </div>

            <div className={styles.createFormContainer}>
                <form className={styles.form} onSubmit={handleSubmit}>
                    {error && <div className={styles.error}>{error}</div>}
                    {success && <div className={styles.success}>{success}</div>}

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Promo Name *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={styles.formInput}
                            placeholder="e.g., Welcome Bonus"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Bonus Amount *</label>
                        <input
                            type="number"
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            className={styles.formInput}
                            placeholder="e.g., 50"
                            step="0.01"
                            min="0"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Promo Type *</label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleInputChange}
                            className={styles.formSelect}
                            required
                        >
                            <option value={PromoType.REGISTRATION}>Registration</option>
                            <option value={PromoType.DEPOSIT}>Deposit</option>
                        </select>
                    </div>

                    {formData.type === PromoType.DEPOSIT && (
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Minimum Deposit *</label>
                            <input
                                type="number"
                                name="minDeposit"
                                value={formData.minDeposit}
                                onChange={handleInputChange}
                                className={styles.formInput}
                                placeholder="e.g., 100"
                                step="0.01"
                                min="0"
                                required
                            />
                        </div>
                    )}

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Expiration Date *</label>
                        <input
                            type="datetime-local"
                            name="expiresAt"
                            value={formData.expiresAt}
                            onChange={handleInputChange}
                            className={styles.formInput}
                            required
                        />
                    </div>

                    {!isShopUser && (
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Select Shops *</label>
                            <input
                                type="text"
                                placeholder="Search shops by name, username, or ID..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className={styles.formInput}
                                style={{ marginBottom: '8px' }}
                            />
                            <button
                                type="button"
                                className={styles.selectAllBtn}
                                onClick={handleSelectAll}
                            >
                                {filteredShops.every(s => formData.shopIds.includes(s.id)) && filteredShops.length > 0
                                    ? 'Deselect All Visible'
                                    : 'Select All Visible'}
                            </button>
                            <div 
                                className={styles.shopSelection}
                                ref={shopSelectionRef}
                                onScroll={handleShopScroll}
                            >
                                {loadingShops && shops.length === 0 ? (
                                    <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                                        Loading shops...
                                    </div>
                                ) : filteredShops.length === 0 ? (
                                    <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                                        {searchQuery ? 'No shops found matching your search' : 'No shops available'}
                                    </div>
                                ) : (
                                    <>
                                        {filteredShops.map((shop) => (
                                            <div key={shop.id} className={styles.shopCheckbox}>
                                                <input
                                                    type="checkbox"
                                                    id={`shop-${shop.id}`}
                                                    checked={formData.shopIds.includes(shop.id)}
                                                    onChange={() => handleShopToggle(shop.id)}
                                                />
                                                <label htmlFor={`shop-${shop.id}`}>
                                                    {shop.name || shop.username} (ID: {shop.id})
                                                </label>
                                            </div>
                                        ))}
                                        {loadingShops && (
                                            <div style={{ padding: '12px', textAlign: 'center', color: '#666', fontSize: '13px' }}>
                                                Loading more shops...
                                            </div>
                                        )}
                                        {!hasMoreShops && shops.length > 0 && searchQuery === '' && (
                                            <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: '12px' }}>
                                                All shops loaded
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            {formData.shopIds.length > 0 && (
                                <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                                    {formData.shopIds.length} shop{formData.shopIds.length !== 1 ? 's' : ''} selected
                                </div>
                            )}
                        </div>
                    )}

                    <div className={styles.formActions}>
                        <button
                            type="button"
                            className={styles.cancelBtn}
                            onClick={handleCancel}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={styles.submitBtn}
                            disabled={submitting}
                        >
                            {submitting ? 'Creating...' : 'Create Promo Codes'}
                        </button>
                    </div>
                </form>
            </div>
        </main>
    );
};

export default PromoCreate;
